import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import {
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Table as MuiTable,
  makeStyles,
} from '@material-ui/core';
import Row from './Row';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import update from 'immutability-helper';
import arePropsEqual from 'fast-deep-equal';
import DraggableRow from './draggableRow';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(theme => ({
  table: {
    '& [class*="MuiTableCell-root"]': {
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${theme.palette.text.primary}`,
    },
  },
  root: {
    marginBottom: '5px',
  },
}));

const Table = ({
  data,
  columns,
  className,
  onDelete,
  onEdit,
  allowEditRow,
  allowDeleteRow,
  showMediaInPortal,
  displayImageInPortal,
  changeAdditionalMarkerText,
  allowDrag,
}) => {
  const { t } = useTranslation();
  const [rows, setrows] = useState(data);
  const previousDataRef = useRef();
  const previousValue = previousDataRef.current;
  // check this of if reorder subtasks and then go inside one of the subtasks and save it , it changes the order of the subtasks and doesnot preserve
  if (
    data &&
    !arePropsEqual(data, previousValue) &&
    !arePropsEqual(data, rows)
  ) {
    setrows(data);
  }

  useEffect(() => {
    // This ensures whenever, data gets changed it also changes this component state without rerendering
    previousDataRef.current = data;
  });

  const moveRow = (dragIndex, hoverIndex) => {
    const dragRow = rows[dragIndex];
    // only change the text incase the dragged sub task has same surfaceImage Id as parent
    changeAdditionalMarkerText(dragIndex, hoverIndex);
    setrows(
      update(rows, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRow],
        ],
      }),
    );
  };

  const renderDraggableRows = (row, index) => {
    return (
      <DraggableRow
        key={index + 1}
        index={index}
        row={row}
        allowEditRow={allowEditRow}
        allowDeleteRow={allowDeleteRow}
        onDelete={onDelete}
        onEdit={onEdit}
        showMediaInPortal={showMediaInPortal}
        displayImageInPortal={displayImageInPortal}
        moveRow={moveRow}
        rowId={index + 1}
        allowDrag={allowDrag}
      />
    );
  };
  const renderNonDraggableRows = (row, index) => {
    return (
      <Row
        key={index + 1}
        index={index}
        row={row}
        allowEditRow={allowEditRow}
        allowDeleteRow={allowDeleteRow}
        onDelete={onDelete}
        onEdit={onEdit}
        showMediaInPortal={showMediaInPortal}
        displayImageInPortal={displayImageInPortal}
        allowDrag={allowDrag}
      />
    );
  };
  const classes = useStyles();
  return (
    <TableContainer className={classes.root}>
      <MuiTable
        className={cn(classes.table, className)}
        aria-label="collapsible table"
      >
        <TableHead>
          <TableRow>
            {columns.map(item => {
              return (
                <TableCell key={item.field || item.headerName} align="center">
                  {t(item.headerName)}
                </TableCell>
              );
            })}
            {(allowEditRow || allowDeleteRow) && (
              <TableCell>{t('Action')}</TableCell>
            )}
          </TableRow>
        </TableHead>
        {allowDrag ? (
          // This is the drag drop context
          <DndProvider backend={HTML5Backend}>
            <TableBody>
              {rows.map((row, index) => {
                return renderDraggableRows(row, index);
              })}
            </TableBody>
          </DndProvider>
        ) : (
          <TableBody>
            {rows.map((row, index) => {
              return renderNonDraggableRows(row, index);
            })}
          </TableBody>
        )}
      </MuiTable>
    </TableContainer>
  );
};

Table.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array,
  className: PropTypes.string,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  allowEditRow: PropTypes.bool,
  allowDrag: PropTypes.bool,
  allowDeleteRow: PropTypes.bool,
  showMediaInPortal: PropTypes.bool,
  displayImageInPortal: PropTypes.func,
  changeAdditionalMarkerText: PropTypes.func,
};

Table.defaultProps = {
  allowDrag: false,
};

export default Table;
